import { css } from "@emotion/react";

// -----------------------------------------------------------------------------
// PALETTE — canonical design tokens (see src/web/svgs/Colors.svg). Alpha
// overlays are sibling keys named `<shade>_<pct>` (e.g. navy[700_20] = 20%).
// -----------------------------------------------------------------------------
export const PALETTE = {
  // Warm neutrals — paper / cream scale (page surfaces, cards, dividers)
  cream: {
    // Warm page shell; paper variants preserve the authored intake/auth surfaces.
    50: "#FFFCF9",
    paper: "#FFFDFB",
    paper_10: "#FFFDFB1A", // paper @ 10% (auth glass panel)
    photo: "#FFFDF9",
    intake: "#FAF6F3",
    payment: "#FAF6F2",
    100: "#F9F3EF",
    200: "#EFE9E4",
    300: "#E0D7CF",
    300_20: "#E0D7CF33", // cream[300] @ 20% (Tabs bg)
    300_47: "#E0D7CF78", // cream[300] @ 47% (Tabs hover)
    400: "#C2B9B2",
    500: "#A49C95",
  },
  // Navy — brand primary scale (text, primary actions, focus)
  navy: {
    25: "#FAFAFD", // cool near-white — page shell surface (lighter step of navy[50])
    50: "#E4E3EC",
    100: "#878EAE",
    300: "#26387A",
    500: "#0F2163", // primary
    500_30: "#0F21634D", // navy[500] @ 30%
    500_50: "#0F216380", // navy[500] @ 50% (radio hover)
    500_70: "#0F2163B3", // navy[500] @ 70%
    700: "#071443", // app text
    700_02: "#07144305", // navy[700] @ 2%
    700_20: "#07144333", // navy[700] @ 20%
    700_60: "#07144399", // navy[700] @ 60%
    900: "#030B2A", // deepest / near-black
    900_08: "#030B2A14", // navy[900] @ 8% (expanded nav shadow)
  },
  // Accents — brand expression: purple / pink / coral / peach / teal / sky
  accent: {
    completionNavyLight: "#302B5D", // Authored completion CTA stop; no canonical navy match.
    completionNavyDeep: "#1B1454", // == editor.buttonHoverDark; independent completion fill.
    purpleDeep: "#470193",
    purple: "#9F3090",
    lavender: "#E1CBFF",
    lilac: "#EBDDFF",
    lilac_60: "#EBDDFF99", // accent.lilac @ 60%
    purple_37: "#9F30905E", // accent.purple @ 37% (Alert border)
    pink: "#F02C55",
    pinkLight: "#FFD0EC",
    pinkPale: "#FCE1F7",
    coral: "#F9776F",
    peach: "#FFBE98",
    peachLight: "#FFDCD1",
    peachPale: "#FFEBE0",
    peachPaleMuted: "#FFF5F0",
    peachLightMuted: "#FFEEE8",
    cream: "#FEE8DA",
    tealDeep: "#0B5246",
    tealPale: "#D7F0EB",
    tealPaleMuted: "#EBF8F5",
    sky: "#D7EEF5",
    skyLight: "#DBF1F8", // scenario callout tint — lighter than accent.sky
    peachWash: "#FEF6F0", // pro-tip callout tint — lighter than accent.peachPale
    lavenderPale: "#F3E9FD", // instruction callout tint — lighter than accent.lilac
    dashboardPeach: "#FECFAB",
    dashboardPurple: "#632BFF",
    intakePeach: "#F7B27E",
    paymentPeach: "#FFCEA8",
    glowAqua: "#90F2FA",
    signupDemoLavender: "#C6CBFF",
    nextStepsPurple: "#D1C2F5",
    nextStepsLilac: "#F0E8FA",
    nextStepsPaper: "#FAF7FC",
    accountCountdown: "#F1E7FA",
    accountAqua: "#83DEFF",
    financialBlue: "#2BA3FF",
    previewBlue: "#2BA3FF",
    previewPink: "#FFD6F3",
    previewLilac: "#C3ABFE",
  },
  // App-level additions (not in Figma's export): white/grey/yellow/black(+alpha),
  // diff (inline diff highlighter), editor (chrome & status colors).
  white: "#FFFFFF",
  white_08: "#FFFFFF14", // white @ 8% (mobile auth glass panel)
  white_20: "#FFFFFF33", // white @ 20% (mobile auth glass border)
  white_30: "#FFFFFF4D", // white @ 30%
  white_40: "#FFFFFF66", // white @ 40% (account card surface)
  white_70: "#FFFFFFB3", // white @ 70% (modal media glass surface)
  white_95: "#FFFFFFF2", // white @ 95%
  // Neutral grey surface tint (general-purpose; editor greys live under editor.*).
  socialProof: {
    ratingText: "#191919",
    scoreBackground: "#EDE7E4",
  },
  grey: {
    100: "#F5F5F5",
    paper: "#F9F9FC",
  },
  yellow: "#FFCF07",
  black: "#000000",
  black_01: "#00000003", // black @ 1%
  black_02: "#00000005", // black @ 2%
  black_03: "#00000008", // black @ 3%
  black_04: "#0000000A", // black @ 4%
  black_08: "#00000014", // black @ 8%
  black_10: "#0000001A", // black @ 10%
  black_15: "#00000026", // black @ 15%
  black_35: "#00000059", // black @ 35%
  black_20: "#00000033", // black @ 20%
  black_30: "#0000004D", // black @ 30%
  black_40: "#00000066", // black @ 40%
  black_50: "#00000080", // black @ 50%
  diff: {
    add: "#38D153",
    add_35: "#38D15359", // diff.add @ 35% — editor inline diff-token
    add_45: "#38D15373", // diff.add @ 45% — clause-level diff bg
    delete: "#FF6363",
    delete_35: "#FF636359", // diff.delete @ 35%
    delete_45: "#FF636373", // diff.delete @ 45%
  },
  // Editor chrome — the Lexical editor's own visual language, grouped here so its
  // literals live in one palette.
  editor: {
    // Chrome greys — toolbar / format buttons / page-break / borders / text
    chromeBg: "#F5F5F5",
    chromeBgDisabled: "#F0F0F0", // format-button disabled / pending
    chromeBgActive: "#DFDFDF",
    chromeBgHover: "#DDDDDD",
    chromeBorderLight: "#E0E0E0",
    chromeBorderMedium: "#D0D0D0",
    chromeText: "#333333",
    chromeTextMuted: "#666666",
    chromeTextSecondary: "#555555",
    chromeTextDisabled: "#999999",
    chromeTextTertiary: "#6B7280", // slightly cooler than chromeTextMuted (ClauseLibraryPanel)
    // Interactive blue family — drag/drop indicators, focus rings, mentions
    interactiveBlue: "#3578E5",
    interactiveBlue_10: "#3578E51A", // drop-target bg
    focusRing: "#1976D2",
    focusRingHover: "#BBDEFB",
    mentionBlue: "#2563EB",
    mentionBlue_22: "#2563EB38", // mention @ 22% (selected)
    pageBreakBlue: "#0078D4",
    pageBreakBlue_15: "#0078D426", // pageBreak @ 15%
    pageBreakBlue_20: "#0078D433", // pageBreak @ 20%
    pageBreakBlueDark: "#0060D0",
    navPillBg: "#EEF1FF", // ClauseLibraryPanel new-PC icon
    navPillFg: "#0D1B4C",
    // Clause-status indicator dots
    statusPending: "#FF8C00",
    statusSaving: "#FFC107",
    statusSaved: "#28A745",
    // Suggestion diff text (accepts / rejects preview inline)
    suggestionAddText: "#15803D",
    suggestionDeleteText: "#C81E1E",
    // Edit history / draft card highlighting
    draftCardBg: "#FFF9E6",
    draftCardBorder: "#FFA500",
    // Find highlighting
    findAllBg: "#B4B4B48C", // grey @ 55% — non-current find matches
    findCurrentBg: "#FCED12F0", // yellow @ 94% — current find match
    findCurrentBgFallback: "#FFF1319B", // yellow @ 61% — fallback highlight
    // Being-dragged clause tint
    beingDraggedBg: "#6C757D1A", // grey @ 10%
    // Chat bubble (SelfUser vs recipient) tints
    chatSelfBubbleBg: "#1363DF14", // blue @ ~8%
    chatRecipientBubbleBg: "#FFF3FE", // pale pink
    // Clause library preview header (grey → hover → active)
    libraryHeaderBg: "#D7DAE4", // rgb(215,218,228)
    libraryHeaderBgHover: "#C4C8D4", // rgb(196,200,212)
    libraryHeaderBgActive: "#899AC8", // rgb(137,154,200)
    // Diff mini-map (side panel showing added/removed regions at a glance)
    diffMiniAdd: "#6ECB8B", // saturated green — distinct from diff.add
    diffMiniDelete: "#EF5350", // saturated red — distinct from diff.delete
    diffMiniTrackBg: "#F1F1F1", // pale grey track
    // Notarization banner (locked-editor banner)
    notarizationBannerBg: "#C7CBD4", // neutral grey
    notarizationBannerText: "#1B254B", // dark navy
    // Personal library editor input border
    personalLibraryBorder: "#D6DAE6",
    // LLM suggestion palette — source for suggestion colors (see llmTheme.ts).
    severity: {
      high: {
        solid: "#9C0E0E", // == PALETTE.semantic.errorDark
        bg: "#FF000024", // red @ 14%
        bgActive: "#FF000040", // red @ 25%
        ring: "#FF000063", // red @ 39%
        bgPanel: "#FFF1F1", // == PALETTE.semantic.errorBg
        badge: "#9C0E0E",
        badgeText: "#FFFFFF",
      },
      medium: {
        solid: "#B34200", // == PALETTE.semantic.warningDark
        bg: "#B342001C", // amber @ 11%
        bgActive: "#B3420038", // amber @ 22%
        ring: "#B3420057", // amber @ 34%
        bgPanel: "#FEF5E7", // pale amber (no palette match)
        badge: "#F6AF43", // amber — llm badge visual identity
        badgeText: "#071443", // == PALETTE.navy[700]
      },
      low: {
        solid: "#9747F4", // purple (distinct from accent.purple #9F3090)
        bg: "#9747F41C", // purple @ 11%
        bgActive: "#9747F433", // purple @ 20%
        ring: "#9747F457", // purple @ 34%
        bgPanel: "#F2ECFE", // pale purple (no palette match)
        badge: "#9E80ED", // mid-purple
        badgeText: "#FFFFFF",
      },
    },
    action: {
      // insert action reuses the semantic.success family via existing
      // COLOR_GREEN_* aliases — no entries needed here.
      modifyBorder: "#9747F440", // purple @ 25%
      modifyBg: "#F4ECFE", // pale purple card bg
      modifyText: "#6B21A8", // deep purple heading
      modifyPillBg: "#FEF3C7", // pale amber
      modifyPillFg: "#92400E", // dark amber
      modifyPillBorder: "#D9770659", // dark amber @ 35%
    },
    suggestionModifiedTint: "#F6AF430F", // amber @ 6% — modified-clause backdrop
    buttonHoverDark: "#1B1454", // deep navy variant — suggestion-accept hover
  },
  // Semantic — success / warning / error scales
  semantic: {
    signupComplete: "#008949", // authored signup field completion green
    errorBg: "#FFF1F1",
    errorPopupBg: "#FFF8F8",
    error: "#DE2A2A",
    errorDark: "#9C0E0E",
    successBg: "#F3FAF5",
    successPopupBg: "#F9FCFA",
    success: "#008770",
    successDark: "#0B5246",
    warningBg: "#FFEBE0",
    warning: "#FFBE98",
    warningDark: "#B34200",
  },
} as const;

// -----------------------------------------------------------------------------
// Named color aliases.
//
// Every alias below is expressed in terms of PALETTE.* whenever the value
// matches a canonical token. Aliases that don't correspond to
// a palette entry are kept as literal hex strings (with an inline note when
// helpful) and are candidates for future palette additions.
// -----------------------------------------------------------------------------

// --- Core brand ------------------------------------------------------------
export const COLOR_PRIMARY = PALETTE.navy[500];
export const COLOR_SECONDARY = PALETTE.accent.coral;
export const COLOR_TERTIARY = PALETTE.white;
export const COLOR_APP_TEXT = PALETTE.navy[700];
export const COLOR_APP_TEXT_LIGHT = PALETTE.navy[700_60];
// Warm paper tone used by the page shell in index.css.
export const COLOR_PAGE_BACKGROUND = PALETTE.cream[50];
export const POST_PURCHASE_MOBILE_GRADIENT = `linear-gradient(180deg, ${PALETTE.accent.nextStepsPurple} 0%, ${PALETTE.accent.nextStepsLilac} 50%, ${PALETTE.accent.nextStepsPaper} 100%)`;

// --- Semantic (error / warning / success) ----------------------------------
export const COLOR_ERROR = PALETTE.semantic.errorDark;
export const COLOR_ERROR_BACKGROUND = PALETTE.semantic.errorBg;
export const COLOR_ERROR_BORDER = PALETTE.semantic.error;
export const COLOR_ERROR_BACKGROUND_POPUP = PALETTE.semantic.errorPopupBg;
export const COLOR_ERROR_COMPONENT_TEXT = PALETTE.semantic.error;
export const COLOR_WARNING = PALETTE.semantic.warning;
export const COLOR_WARNING_TEXT = PALETTE.semantic.warningDark;
export const COLOR_WARNING_TRIANGLE = PALETTE.yellow;
export const COLOR_SUCCESS = PALETTE.semantic.success;
export const COLOR_GREEN_TEXT = PALETTE.semantic.successDark;
export const COLOR_GREEN_BORDER = PALETTE.semantic.success;
export const COLOR_GREEN_BACKGROUND = PALETTE.semantic.successBg;
export const COLOR_GREEN_CHECKBOX_HOVER = PALETTE.semantic.successBg;
export const COLOR_PROMO_CODE_APPLIED_GREEN_BACKGROUND =
  PALETTE.accent.tealPale;
export const COLOR_PROMO_CODE_APPLIED_GREEN_TEXT = PALETTE.accent.tealDeep;
export const COLOR_PROMO_CODE_ERROR = PALETTE.accent.purple;

export const COLOR_PRODUCT_INCLUDED_IN_BUNDLE_TEXT = PALETTE.semantic.success;
export const COLOR_PAID_TEXT = PALETTE.semantic.success;
export const COLOR_DUE_TEXT = PALETTE.accent.pink;

// --- Info / link / neutrals ------------------------------------------------
export const COLOR_INFO = PALETTE.accent.cream;
export const COLOR_LINK = PALETTE.navy[500];

export const COLOR_DIVIDER = PALETTE.navy[50];

export const COLOR_GREY_BORDER = PALETTE.black_20; // semantic "generic border" — semi-transparent black reads as neutral
export const COLOR_GREY_BACKGROUND = PALETTE.cream[300];
export const COLOR_GREY_01 = PALETTE.navy[100];
export const COLOR_DESCRIPTION_TEXT = PALETTE.navy[100];

// --- Brand accents (map to PALETTE.accent.*) -------------------------------
export const COLOR_LILAC = PALETTE.accent.purpleDeep;
export const COLOR_BRAND_LAVENDER = PALETTE.accent.lavender;
export const COLOR_BRAND_LILAC = PALETTE.accent.lilac;
export const COLOR_PURPLE_BACKGROUND = PALETTE.accent.lilac;

export const COLOR_PURPLE_BORDER = PALETTE.accent.purple;
export const COLOR_PURPLE_TEXT = PALETTE.accent.purple;
export const COLOR_CAUTION_BACKGROUND = PALETTE.accent.cream;
export const COLOR_STARS_CONTAINER_BACKGROUND = PALETTE.accent.tealPale;

// --- Menus / navigation / dropdowns ---------------------------------------
export const COLOR_MENU_HOVER = PALETTE.accent.lilac;
export const COLOR_LIGHT_BLUE_CHECKBOX_HOVER = PALETTE.accent.sky;
export const COLOR_DROPDOWN_FILTER_BACKGROUND = PALETTE.cream[100];
export const COLOR_DROPDOWN_TEXT_SELECTED = PALETTE.accent.purple;
export const COLOR_CLOSE_BUTTON_BACKGROUND = PALETTE.cream[100];

export const COLOR_NAV_EXPANDED_BACKGROUND = COLOR_PAGE_BACKGROUND;
export const COLOR_NAV_ITEM_ACTIVE_BACKGROUND = PALETTE.accent.lilac;
// Hover deliberately previews the active fill, so it is the same lilac rather
// than a separate tint. Kept as its own role-named alias so hover can diverge
// later without touching the call sites.
export const COLOR_NAV_ITEM_HOVER_BACKGROUND = PALETTE.accent.lilac;
// Nav lock indicator sits on top of both `COLOR_NAV_EXPANDED_BACKGROUND`
// (cream[50]) and `COLOR_NAV_ITEM_ACTIVE_BACKGROUND` (accent.lilac). The
// previous cream[400] (#C2B9B2) failed WCAG's 3:1 non-text contrast on both
// surfaces. navy[100] is the muted-cool step already used for descriptive
// text and grey borders; it clears 3:1 against cream[50] and lilac while still
// reading as a de-emphasized disabled indicator.
export const COLOR_NAV_LOCK_ICON = PALETTE.navy[100];

// --- Dashboard checklist ---------------------------------------------------
export const COLOR_DASHBOARD_BACKGROUND = PALETTE.cream.intake;
export const COLOR_QUESTION_CARD_BACKGROUND = PALETTE.cream.paper;
// A darker warm cream step than `COLOR_PAGE_BACKGROUND`, so checklist rows stay
// distinct from the page shell.
export const COLOR_CHECKLIST_ROW_BACKGROUND = PALETTE.cream[100];
export const COLOR_CHECKLIST_ROW_HOVER_BACKGROUND = PALETTE.cream[200];

export const COLOR_LOCKED_PROCESS_BADGE_BACKGROUND = PALETTE.navy[50];
export const COLOR_ONBOARDING_REASSURANCE_BACKGROUND =
  PALETTE.accent.lavenderPale;

// --- Domain-specific tints -------------------------------------------------
export const COLOR_ACCOUNT_COUNTDOWN_BACKGROUND =
  PALETTE.accent.accountCountdown;
export const COLOR_QUESTIONNAIRE_EDUCATION_BACKGROUND = PALETTE.cream[100];
export const COLOR_QUESTIONNAIRE_SCENARIO_BACKGROUND = PALETTE.accent.skyLight;
export const COLOR_QUESTIONNAIRE_PROTIP_BACKGROUND = PALETTE.accent.peachWash;
export const COLOR_QUESTIONNAIRE_INFORMATION_BACKGROUND =
  PALETTE.accent.lavenderPale;
// Figma uses a warm inset surface for the notarization flow rather than the
// canonical white elevated card surface (§5.5).
export const COLOR_NOTARIZATION_PANEL_BACKGROUND = PALETTE.cream[100];
export const COLOR_NOTARIZATION_UPLOAD_DRAG_BACKGROUND =
  PALETTE.accent.lilac_60;
export const COLOR_NOTARIZATION_METHOD_INACTIVE = PALETTE.cream[400];
// Figma specifies a teal referral surface rather than §5.5's white elevated card.
export const COLOR_REFERRAL_CARD_BACKGROUND = PALETTE.accent.tealPale;
// The generated-document completion panel uses the same intentional warm inset
// treatment while remaining separate from the notarization domain.
export const COLOR_DOCUMENT_COMPLETION_BACKGROUND = PALETTE.cream[100];
export const COLOR_DOCUMENT_REGENERATION_BACKGROUND =
  PALETTE.accent.lavenderPale;
export const COLOR_DOCUMENT_REGENERATION_ICON_BACKGROUND = PALETTE.accent.lilac;
export const COLOR_FINANCIAL_ITEM_BACKGROUND = PALETTE.cream[100];
export const COLOR_FINANCIAL_ITEM_HEADER_BACKGROUND = PALETTE.cream[100];
export const COLOR_PRIMARY_BUTTON = PALETTE.navy[500];
export const COLOR_FD_FLASH = PALETTE.accent.lilac;

export const COLOR_FD_SUMMARY_TOTAL_BACKGROUND = PALETTE.accent.skyLight;
export const COLOR_FD_SUMMARY_NET_WORTH_BACKGROUND = PALETTE.accent.tealPale;
// No palette token matches the joint tag's warm tint; use peachPale.
export const COLOR_FD_SUMMARY_TAG_JOINT_BACKGROUND = PALETTE.accent.peachPale;
export const COLOR_FD_SUMMARY_TAG_SEPARATE_BACKGROUND = PALETTE.navy[50];
export const COLOR_FD_SUMMARY_STEP_INACTIVE = PALETTE.navy[100];
export const COLOR_FD_TABLE_ROW_BORDER = PALETTE.black_10;
export const COLOR_FD_CATEGORY_ICON_BACKGROUND = PALETTE.grey.paper;
export const COLOR_FD_SUPPORT_BORDER_START = PALETTE.accent.lavender;
export const COLOR_FD_SUPPORT_BORDER_END = PALETTE.accent.peach;
export const COLOR_YELLOW_STAR = PALETTE.accent.peach; // peach, not yellow
export const COLOR_BLUE_TEXT = PALETTE.navy[300]; // navy — palette has no true blue
export const COLOR_UNDER_REVIEW_TEXT = PALETTE.accent.tealDeep;
export const COLOR_DEACTIVATED_TEXT = PALETTE.accent.pink;

export const COLOR_OVERLAY_BACKGROUND = PALETTE.navy[700_20];
export const COLOR_MODAL_GLASS_BACKGROUND = PALETTE.white_70;
export const COLOR_CART_SUMMARY_FEATURES_BACKGROUND = PALETTE.navy[700_02];

export const COLOR_ATTORNEY_CARD_PRODUCT_REPRESENTATION_BACKGROUND =
  PALETTE.white;
export const COLOR_ATTORNEY_SERVICE_CARD_BACKGROUND = PALETTE.accent.lilac;
export const COLOR_ATTORNEY_SERVICE_ICON_BACKGROUND_Q_A =
  PALETTE.accent.lilac_60;
export const COLOR_ATTORNEY_SERVICE_ICON_BACKGROUND_REPRESENTATION =
  PALETTE.accent.lavender;
export const COLOR_ATTORNEY_SERVICE_TAG_BACKGROUND = PALETTE.accent.pinkPale;
export const COLOR_GENERIC_SERVICE_CARD_BORDER = PALETTE.accent.lavender;

// --- Diff highlighting -----------------------------------------------------
// Diff green/red don't map to brand colors; kept in PALETTE.diff for locality.
export const COLOR_DIFF_ADD_BACKGROUND = PALETTE.diff.add_45;
export const COLOR_DIFF_DELETE_BACKGROUND = PALETTE.diff.delete_45;

// --- Form inputs -----------------------------------------------------------
export const COLOR_INPUT_DEFAULT_BORDER = PALETTE.navy[100];
export const COLOR_INPUT_BORDER_FOCUSED = PALETTE.navy[500];
export const COLOR_INPUT_BORDER_HOVER = PALETTE.navy[500_70];
export const COLOR_SECONDARY_BUTTON_DISABLED_BORDER = PALETTE.navy[50];
export const COLOR_SECONDARY_BUTTON_DISABLED_TEXT = PALETTE.navy[500_30];

// -----------------------------------------------------------------------------
// Button / input solid colors (rounded to PALETTE.* where possible).
// -----------------------------------------------------------------------------
export const BUTTON_SECONDARY_COLOR = PALETTE.white;
export const BUTTON_DISABLED_COLOR = PALETTE.navy[50];
export const BUTTON_GREY_BORDER = PALETTE.cream[200];
export const BUTTON_FILTER_SELECTED_COLOR = PALETTE.navy[50];
export const BUTTON_FILTER_HOVER_COLOR = PALETTE.accent.lilac;
export const PLACEHOLDER_TEXT_COLOR = PALETTE.navy[700_60];
export const DISABLED_INPUT_FIELD_COLOR = PALETTE.cream[100];

// -----------------------------------------------------------------------------
// Gradients — every stop rounded to the nearest PALETTE.* entry.
//
// Where two stops collapsed to the same palette entry after rounding, I've
// picked adjacent-family entries to preserve visible gradation.
// -----------------------------------------------------------------------------

// --- Button hover gradients ------------------------------------------------
export const BUTTON_PRIMARY_GRADIENT = `linear-gradient(90deg, ${PALETTE.accent.coral} -0.12%, ${PALETTE.accent.purple} 41.07%, ${PALETTE.accent.purpleDeep} 95.17%)`;
export const BUTTON_PRIMARY_GRADIENT_HOVER = `linear-gradient(97deg, ${PALETTE.accent.peachPale} 15.19%, ${PALETTE.accent.lilac} 80.05%)`;
export const DANGER_BUTTON_GRADIENT = // kept alpha; pink → error, both @ 50%
  "linear-gradient(290deg, rgba(240, 44, 85, 0.50) -11.95%, rgba(222, 42, 42, 0.50) 42.48%)";

// --- Semantic gradients ---------------------------------------------------
// The completion success CTA intentionally uses its authored navy gradient at rest.
export const COMPLETION_BUTTON_GRADIENT = `linear-gradient(90deg, ${PALETTE.accent.completionNavyLight}, ${PALETTE.accent.completionNavyDeep})`;
export const WARNING_GRADIENT =
  // pale peach → light peach
  `linear-gradient(126.28deg, ${PALETTE.accent.peachPale} 12.24%, ${PALETTE.accent.peachLight} 96.31%)`;
export const SUCCESS_GRADIENT =
  // pale success bg → pale teal
  `linear-gradient(126.28deg, ${PALETTE.semantic.successBg} 12.24%, ${PALETTE.accent.tealPale} 96.31%)`;
// The design's #FFF2E7 → #E8D6F3 stops have no exact palette entries; use the
// nearest peachPale → lilac brand tokens.
export const NOTARIZATION_UPLOAD_GRADIENT = `linear-gradient(91deg, ${PALETTE.accent.peachPale}, ${PALETTE.accent.lilac})`;
// Deeper than WARNING_GRADIENT; the design's #FFDFDF → #FFC9B8 stops have no
// palette entries, so both snap to their nearest warm accents.
export const REFUND_GRADIENT = `linear-gradient(126.28deg, ${PALETTE.accent.peachLight} 12.24%, ${PALETTE.accent.peach} 96.31%)`;

// --- Icon / container gradients -------------------------------------------
export const ICON_PRIMARY_GRADIENT_SELECTED = `linear-gradient(139deg, ${PALETTE.accent.peachPale} -4.24%, ${PALETTE.accent.lavender} 102.92%)`;
export const ICON_HEADER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(320deg, ${PALETTE.accent.sky} -47.63%, ${PALETTE.accent.lilac} 87.71%)`;
export const ICON_BLUE_CONTAINER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(320deg, ${PALETTE.accent.sky} -47.63%, ${PALETTE.accent.tealPale} 87.71%)`;
export const ICON_PEACH_CONTAINER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(165deg, ${PALETTE.accent.peachLight} 4.92%, ${PALETTE.accent.coral} 89.51%)`;
export const ICON_AQUA_CONTAINER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(163deg, ${PALETTE.accent.sky} -1.98%, ${PALETTE.accent.tealPale} 88.33%)`;
export const ICON_LIGHT_PINK_CONTAINER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(145deg, ${PALETTE.accent.pinkPale} 5.09%, ${PALETTE.accent.lavender} 92.36%)`;
export const BLUE_PURPLE_CONTAINER_BACKGROUND_COLOR_GRADIENT = `linear-gradient(94deg, ${PALETTE.accent.sky} 6.03%, ${PALETTE.accent.lilac} 108.19%)`;
export const VIDEO_CONTAINER_COLOR_GRADIENT = `linear-gradient(90deg, ${PALETTE.accent.lilac} -6.68%, ${PALETTE.accent.sky} 106.11%)`;
export const CLIENT_ICON_BACKGROUND_GRADIENT = `linear-gradient(139deg, ${PALETTE.accent.sky} -4.24%, ${PALETTE.accent.tealPale} 102.92%)`;
export const ICON_OCEAN_MIST_COLOR_GRADIENT = `linear-gradient(165deg, ${PALETTE.accent.sky} 4.92%, ${PALETTE.accent.tealPale} 89.51%)`;

// --- Popup / banner backgrounds -------------------------------------------
export const INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT =
  COLOR_TERTIARY;
export const WARNING_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT = `linear-gradient(126.07deg, ${PALETTE.accent.peachPaleMuted} 19.39%, ${PALETTE.accent.peachLightMuted} 83.3%)`;
export const SUCCESS_POPUP_BACKGROUND_COLOR_GRADIENT = `linear-gradient(180deg, ${PALETTE.semantic.successPopupBg} 0%, ${PALETTE.accent.tealPaleMuted} 100%)`;

export const SECTION_ACCESS_BLOCKER_BACKGROUND_COLOR_GRADIENT =
  // pale peach → pale blue
  `linear-gradient(111deg, ${PALETTE.accent.peachPale} 13.58%, ${PALETTE.accent.sky} 98.42%)`;

// Vertical fade confines the palette tint to the page header.
const previewWash = (from: string, to: string) =>
  [
    `linear-gradient(180deg, transparent 0%, ${COLOR_PAGE_BACKGROUND} 360px)`,
    `linear-gradient(90deg, ${COLOR_PAGE_BACKGROUND} 0%, ${from} 25%, ${to} 63%, ${COLOR_PAGE_BACKGROUND} 96%)`,
  ].join(", ");

export const DASHBOARD_PAGE_GRADIENT = `linear-gradient(90deg, ${PALETTE.accent.dashboardPeach} 25%, ${PALETTE.accent.dashboardPurple} 100%)`;
export const DASHBOARD_PAGE_GRADIENT_BLUR = "225px";
export const PAYMENT_CONFIRMATION_COLORS = {
  background: PALETTE.cream.payment,
  purple: PALETTE.accent.dashboardPurple,
  peach: PALETTE.accent.paymentPeach,
  aqua: PALETTE.accent.glowAqua,
} as const;
export const INTAKE_DESKTOP_GRADIENT = `linear-gradient(226.7deg, ${PALETTE.accent.dashboardPurple} 34.37%, ${PALETTE.accent.intakePeach} 59.41%, ${PALETTE.accent.glowAqua} 76%)`;
export const INTAKE_MOBILE_GRADIENT = `linear-gradient(90deg, ${PALETTE.accent.dashboardPurple} 0%, ${PALETTE.accent.intakePeach} 52.88%, ${PALETTE.accent.glowAqua} 98.08%)`;
export const ACCOUNT_MOBILE_GRADIENT = `linear-gradient(227.13deg, ${PALETTE.accent.dashboardPurple} 34.26%, ${PALETTE.accent.dashboardPeach} 61.76%, ${PALETTE.accent.accountAqua} 85.26%)`;

export const PREVIEW_QUESTIONNAIRE_GRADIENT = `linear-gradient(227.12692deg, ${PALETTE.accent.dashboardPeach} 36.75772%, ${PALETTE.accent.previewPink} 85.25791%)`;

export const FINANCIAL_DISCLOSURE_PAGE_GRADIENT = previewWash(
  PALETTE.accent.tealPale,
  PALETTE.accent.sky,
);
export const FINANCIAL_DISCLOSURE_GLOW_GRADIENT = `linear-gradient(227.126924deg, ${PALETTE.accent.financialBlue} 34.257715%, ${PALETTE.accent.dashboardPeach} 61.757817%, ${PALETTE.accent.accountAqua} 85.257905%)`;
export const PREVIEW_QUESTIONNAIRE_MOBILE_GRADIENT = `linear-gradient(146.30993deg, ${PALETTE.accent.dashboardPeach} 32%, ${PALETTE.accent.previewPink} 68%)`;
export const PREVIEW_FINANCIAL_DISCLOSURE_GRADIENT = `linear-gradient(227.12692deg, ${PALETTE.accent.previewBlue} 34.25771%, ${PALETTE.accent.dashboardPeach} 61.75782%, ${PALETTE.accent.accountAqua} 85.25791%)`;
export const PREVIEW_ALIGNMENT_GRADIENT = `linear-gradient(227.12692deg, ${PALETTE.accent.dashboardPurple} 34.25771%, ${PALETTE.accent.dashboardPeach} 61.75782%)`;
export const PREVIEW_DOCUMENT_GENERATION_GRADIENT = `linear-gradient(234.81582deg, ${PALETTE.accent.previewLilac} 17.15267%, ${PALETTE.accent.previewPink} 83.09786%)`;
export const PREVIEW_REVIEW_NOTARIZE_GRADIENT = `linear-gradient(227.12692deg, ${PALETTE.accent.dashboardPeach} 61.75782%, ${PALETTE.accent.accountAqua} 85.25791%)`;
export const PREVIEW_ATTORNEY_SERVICES_GRADIENT = PREVIEW_ALIGNMENT_GRADIENT;

// --- Tag / skeleton / stripe ----------------------------------------------
export const SKELETON_BACKGROUND_GRADIENT = `linear-gradient(139deg, ${PALETTE.cream[300]} 3.95%, ${PALETTE.navy[100]} 94.47%)`;
export const REPRESENTATION_TAG_GRADIENT =
  // pale blue → pale lavender
  `linear-gradient(94deg, ${PALETTE.accent.sky} 6.03%, ${PALETTE.accent.lavender} 108.19%)`;
export const Q_A_TAG_GRADIENT =
  // pale purple → pale pink
  `linear-gradient(94deg, ${PALETTE.accent.lavender} 6.03%, ${PALETTE.accent.pinkPale} 108.19%)`;
// Hardcoded Stripe brand colors (blue → purple) — intentionally NOT palette
// tokens so the Stripe logo box can't drift from Stripe's actual brand colors.
export const STRIPE_LOGO_BOX_GRADIENT =
  "linear-gradient(320deg, #9CBEF9 0%, #9A2AE6 100%)";

// --- Cart / attorney service / process overview ---------------------------
export const CART_SUMMARY_BACKGROUND_COLOR_GRADIENT = // pale peach → lavender (91% alpha preserved on stop 1)
  "linear-gradient(156deg, rgba(255, 235, 224, 0.91) 8.53%, #E1CBFF 107.19%)"; // peachPale @ 91% → lavender
export const CART_SUMMARY_FLOATING_BACKGROUND_COLOR_GRADIENT =
  // cream → pale lilac
  `linear-gradient(96deg, ${PALETTE.cream[50]} -40.84%, ${PALETTE.accent.lilac} 115.61%)`;
export const ATTORNEY_SERVICES_CARD_BACKGROUND_COLOR_GRADIENT =
  // pale peach → lavender
  `linear-gradient(${PALETTE.accent.peachPale} 8.53%, ${PALETTE.accent.lavender} 107.19%)`;
export const PROCESS_OVERVIEW_BACKGROUND_COLOR_GRADIENT =
  // pale peach → lavender
  `linear-gradient(171deg, ${PALETTE.accent.peachPale} -3.45%, ${PALETTE.accent.lavender} 105.31%)`;

// -----------------------------------------------------------------------------
// FONTS — Area Normal (brand, weights 300–700) + PP Editorial New (serif,
// Italic-400 only, used inline by `Accent`).
// -----------------------------------------------------------------------------
// `AN` is the raw brand font; the semantic names reference it, so swapping the
// brand font is a one-line change.
const AN = "'Area Normal', sans-serif";
export const HEADER_FONT_FAMILY = AN;
export const TEXT_FONT_FAMILY = AN;
export const EMPHASIS_FONT_FAMILY = "'PP Editorial New', 'Area Normal', serif";

// -----------------------------------------------------------------------------
// FONT_SIZE — scale for non-prose UI (buttons, inputs, icons); for prose /
// headings prefer typographyStyles.* below.
// -----------------------------------------------------------------------------
export const FONT_SIZE = {
  xxs: "8px",
  xs: "12px",
  sm: "13px",
  md: "14px",
  lg: "15px",
  xl: "16px",
  xxl: "18px",
  xxxl: "24px",
} as const;

// -----------------------------------------------------------------------------
// FONT_WEIGHT — canonical weight scale (Area Normal ships 300–700).
// -----------------------------------------------------------------------------
export const FONT_WEIGHT = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const BORDER_WIDTH = {
  thin: "1px",
  focus: "1.5px",
} as const;

// -----------------------------------------------------------------------------
// RADII — canonical border-radius scale
// -----------------------------------------------------------------------------
export const RADII = {
  none: "0",
  xs: "4px",
  sm: "8px",
  smMd: "12px",
  md: "14px",
  lg: "16px",
  xl: "24px",
  xxl: "32px",
  previewIllustration: "1.77778% / 3.75%",
  circle: "50%", // fully circular (avatars / round icon buttons)
  pill: "9999px", // fully rounded ends (pills / badges)
} as const;

export const BORDER_RADIUS_DEFAULT = RADII.md;

export const MOBILE_BREAKPOINT = 950;
export const MOBILE_SCREEN = `@media screen and (max-width: ${MOBILE_BREAKPOINT}px)`;

export const TWO_COLUMN_CONTAINER_BREAKPOINT = 1120;
export const TWO_COLUMN_CONTAINER_SCREEN = `@media screen and (max-width: ${TWO_COLUMN_CONTAINER_BREAKPOINT}px)`;

// Semantic alias for typography responsiveness. Figma's "Medium (Tablet)" band
// applies below 1120px; "Small (Mobile)" (MOBILE_SCREEN) then overrides below 950px.
export const TABLET_BREAKPOINT = TWO_COLUMN_CONTAINER_BREAKPOINT;
export const TABLET_SCREEN = TWO_COLUMN_CONTAINER_SCREEN;

export const PAYMENT_PAGE_FIRST_BREAKPOINT = 1304;
export const PAYMENT_PAGE_FIRST_BREAKPOINT_SCREEN = `@media screen and (max-width: ${PAYMENT_PAGE_FIRST_BREAKPOINT}px)`;

export const PAYMENT_PAGE_SECOND_BREAKPOINT = 816;
export const PAYMENT_PAGE_SECOND_BREAKPOINT_SCREEN = `@media screen and (max-width: ${PAYMENT_PAGE_SECOND_BREAKPOINT}px)`;

export const NAV_Z_INDEX = 8;
export const HEADER_Z_INDEX = 7;

export const OVERLAY_Z_INDEX = 1000;

// Height of the fixed page chrome: desktop header strip and mobile nav bar match.
export const HEADER_HEIGHT = "56px";
export const DESKTOP_NAV_WIDTH = "80px";
export const CUSTOMER_NAV_EXPANDED_WIDTH = 310;

export interface IconProps {
  size?: IconSize | SkeletonIconSize;
  color?: string;
}

export enum IconSize {
  XS = "12",
  S = "16",
  M = "20",
  L = "24",
  XL = "32",
  XXL = "56",
  XXXL = "80",
}

export enum SkeletonIconSize {
  S = "72",
  L = "200",
}

// Color constants for icon backgrounds
export const GROUP_ICON_COLORS = {
  BLUE_PURPLE: ICON_HEADER_BACKGROUND_COLOR_GRADIENT,
  BLUE: ICON_BLUE_CONTAINER_BACKGROUND_COLOR_GRADIENT,
  PEACH: ICON_PEACH_CONTAINER_BACKGROUND_COLOR_GRADIENT,
  AQUA: ICON_AQUA_CONTAINER_BACKGROUND_COLOR_GRADIENT,
  LIGHT_PINK: ICON_LIGHT_PINK_CONTAINER_BACKGROUND_COLOR_GRADIENT,
} as const;

// Common Values
export enum SPACING {
  XXS = "2px",
  XS = "4px",
  SM = "8px",
  SM_MD = "12px", // intermediate step between SM (8px) and MD (16px)
  MD = "16px",
  LG = "24px",
  LG_XL = "32px", // intermediate step between LG (24px) and XL (40px)
  XL = "40px",
  XXL = "64px",
}

export const QUESTIONNAIRE_BOTTOM_SPACING_DESKTOP = "72px";
export const QUESTIONNAIRE_BOTTOM_SPACING_MOBILE = "88px";

export const BORDER_RADIUS_MEDIUM = RADII.lg;

export const FLEX_COLUMN = css`
  display: flex;
  flex-direction: column;
`;

export const FLEX_ROW = css`
  display: flex;
  flex-direction: row;
`;

// Shared buttons need border-box because there is no global box-sizing reset.
export const FULL_WIDTH_BUTTON = css`
  box-sizing: border-box;
  width: 100%;
`;

// §2.6.1: an <a> inherits its color only if both `color` and `:visited` ask.
// Consumers: typography/Link.tsx (`ExternalLink`), pages/home/ChecklistSection.tsx
// (`CHECKLIST_ROW`), layout/utils/desktop/components.tsx (`NAV_ROW`).
export const INHERIT_LINK_COLOR = css`
  color: inherit;

  &:visited {
    color: inherit;
  }
`;

/** Removes only UA button chrome while preserving an existing typography token. */
export const BUTTON_CHROME_RESET = css`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

// A <button> that carries none of the UA's control chrome, so a real button can
// wear a non-control visual recipe (§4.4: the click handler must live on a
// button even when the thing looks like a nav row or a link).
//
// The last two declarations are not resets — they're cascade opt-ins, and both
// are sanctioned exceptions rather than oversights:
//   font: inherit   — §3.4's form-control carve-out. Opts the button back into
//                     the inherited brand font instead of pinning a family.
//   color: inherit  — §2.6.1. `:root`'s text color does not reach a <button>,
//                     so the button has to ask for it.
//
// `display`, `width` and `cursor` are deliberately absent: those are layout
// choices, not UA chrome, and the consumers differ on all three.
// Consumers: layout/utils/desktop/components.tsx (`NavLinkButton`),
// layout/NavCheckboxItem.tsx (`NavCheckboxRowButton`).
export const UNSTYLED_BUTTON = css`
  ${BUTTON_CHROME_RESET}
  font: inherit;
  color: inherit;
`;

// Drops text out of the visual layout while keeping it in the accessibility
// tree — so not `display: none` or `visibility: hidden`, which drop it from
// both. Only for text that genuinely can't be shown; visible is better (§4.5).
export const VISUALLY_HIDDEN = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

// The pill surface of a nav row, shared by the desktop rail (`NavItem`) and the
// mobile drawer (`DrawerItem`). `border-radius` lives here rather than on
// `NAV_ROW` because this is the element that paints the background it clips.
export const NAV_ROW_SURFACE = ({ isActive }: { isActive?: boolean }) => css`
  ${FLEX_ROW}
  align-items: center;
  border-radius: ${RADII.pill};
  transition: all 100ms ease-in-out;

  ${isActive &&
  css`
    background: ${COLOR_NAV_ITEM_ACTIVE_BACKGROUND};
  `}

  &:hover {
    background: ${COLOR_NAV_ITEM_HOVER_BACKGROUND};
  }
`;

// Makes a nav row inert and dims it. The attorney rail's out-of-stage rows
// only: their destinations aren't registered routes at that stage, so there is
// no page to land on and explain the gate. A *customer* row is never this —
// it stays a live link and the lock icon is the whole signal (§8.1).
//
// Takes a boolean rather than emitting only when set, because it is applied to
// a wrapper that is also rendered in the enabled state; the `auto`/`1`/`pointer`
// branch is what restores the default when the row is reachable.
export const NAV_ROW_INERT = (isDisabled: boolean | undefined) => css`
  pointer-events: ${isDisabled ? "none" : "auto"};
  opacity: ${isDisabled ? 0.5 : 1};
  cursor: ${isDisabled ? "default" : "pointer"};
`;

export const QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT = "Enter answer here…";

// -----------------------------------------------------------------------------
// TYPOGRAPHY — responsive type scale (source: src/web/svgs/Typography.svg).
// Prefer the prose components (typography/Paragraph.tsx, Header.tsx); use these
// raw mixins only for non-prose primitives (inputs, selects, buttons).
// -----------------------------------------------------------------------------
// Shared bases: each carries the family, tracking, and the most-common
// size/weight/line-height; the tokens below declare only their deltas.
const headingBase = css`
  font-family: ${HEADER_FONT_FAMILY};
  letter-spacing: -0.04em;
  font-weight: ${FONT_WEIGHT.semibold};
  line-height: 130%;
`;
const bodyBase = css`
  font-family: ${TEXT_FONT_FAMILY};
  letter-spacing: -0.01em;
  font-size: 15px;
  font-weight: ${FONT_WEIGHT.light};
  line-height: 160%;
`;

// Responsive scales extend headingBase/bodyBase with only their deltas.
// General body tokens stay global; authored scene variants include their mobile scales.
export const typographyStyles = {
  financialDisclosureHeading: css`
    font-size: 36px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 1.2;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 28px;
    }
  `,
  financialWelcomeHeading: css`
    font-size: 32px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 1.2;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 28px;
    }
  `,
  // Completion and referral scenes use their authored desktop/mobile scales.
  completionHeading: css`
    ${headingBase}
    font-size: 40px;
    line-height: 41px;
    letter-spacing: 0;
    ${MOBILE_SCREEN} {
      font-size: 32px;
    }
  `,
  referralHeading: css`
    ${headingBase}
    font-size: 40px;
    line-height: 49px;
    letter-spacing: 0;
    ${MOBILE_SCREEN} {
      font-size: 28px;
      line-height: 34px;
      letter-spacing: -0.01em;
    }
  `,
  referralCardHeading: css`
    ${headingBase}
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 22px;
      line-height: 26px;
      letter-spacing: 0;
    }
  `,
  completionDescription: css`
    ${bodyBase}
    font-size: 18px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 20px;
    letter-spacing: 0;
    ${MOBILE_SCREEN} {
      font-size: 14px;
    }
  `,
  completionBenefit: css`
    ${bodyBase}
    font-size: 14px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 20px;
    letter-spacing: 0;
    ${MOBILE_SCREEN} {
      font-size: 12px;
      line-height: 18.5px;
    }
  `,
  referralNavigationLabel: css`
    ${bodyBase}
    font-size: 16px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 19px;
    letter-spacing: 0;
  `,
  referralIntro: css`
    ${bodyBase}
    font-size: 16px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 20px;
    letter-spacing: 0;
    ${MOBILE_SCREEN} {
      font-size: 15px;
      font-weight: ${FONT_WEIGHT.medium};
      line-height: 22px;
    }
  `,
  referralCardDescription: css`
    ${bodyBase}
    font-size: 17px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 1.6;
    ${MOBILE_SCREEN} {
      font-size: 15px;
      font-weight: ${FONT_WEIGHT.regular};
      line-height: 22px;
      letter-spacing: 0;
    }
  `,
  immigrationModalTitle: css`
    font-size: 36px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 110%;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 28px;
    }
  `,
  immigrationModalBody: css`
    font-size: 17px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 160%;
  `,
  // Account title has its own desktop/mobile scale, independent of the tablet heading scale.
  accountHeading: css`
    font-size: 48px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 1.2;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 22px;
    }
  `,
  // Bespoke Figma headings keep their approved optical weights without inheriting tablet scale changes.
  checklistHeading: css`
    font-size: 32px;
    font-weight: ${FONT_WEIGHT.medium};
    line-height: 1.2;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 20px;
      line-height: 1.3;
    }
  `,
  nextStepsHeading: css`
    font-size: 32px;
    font-weight: ${FONT_WEIGHT.medium};
    line-height: 1.2;
    letter-spacing: -0.02em;
    ${MOBILE_SCREEN} {
      font-size: 24px;
      font-weight: ${FONT_WEIGHT.semibold};
    }
  `,
  paymentConfirmationHeading: css`
    font-size: 48px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 1.2;
    letter-spacing: 0;
    ${TABLET_SCREEN} {
      letter-spacing: -0.03em;
    }
    ${MOBILE_SCREEN} {
      font-size: 36px;
      letter-spacing: -0.02em;
    }
  `,
  // --- Headings ---
  h1: css`
    ${headingBase}
    font-size: 44px;
    font-weight: ${FONT_WEIGHT.regular};
    line-height: 110%;
    ${TABLET_SCREEN} {
      font-size: 52px;
      font-weight: ${FONT_WEIGHT.semibold};
      letter-spacing: -0.03em;
    }
    ${MOBILE_SCREEN} {
      font-size: 36px;
    }
  `,
  h2: css`
    ${headingBase}
    font-size: 40px;
    font-weight: ${FONT_WEIGHT.regular};
    line-height: 120%;
    ${TABLET_SCREEN} {
      font-size: 36px;
      font-weight: ${FONT_WEIGHT.medium};
    }
    ${MOBILE_SCREEN} {
      font-size: 28px;
    }
  `,
  h3: css`
    ${headingBase}
    font-size: 32px;
    font-weight: ${FONT_WEIGHT.medium};
    line-height: 120%;
    ${TABLET_SCREEN} {
      font-size: 30px;
    }
    ${MOBILE_SCREEN} {
      font-size: 26px;
    }
  `,
  h4: css`
    ${headingBase}
    font-size: 24px;
    ${TABLET_SCREEN} {
      line-height: 120%;
    }
    ${MOBILE_SCREEN} {
      font-size: 18px;
      line-height: 130%;
    }
  `,
  h5: css`
    ${headingBase}
    font-size: 20px;
    ${TABLET_SCREEN} {
      font-size: 18px;
    }
    ${MOBILE_SCREEN} {
      font-size: 17px;
    }
  `,
  // H6 is only defined for desktop in Figma; tablet mirrors it, mobile drops to 17px.
  h6: css`
    ${headingBase}
    font-size: 18px;
    ${MOBILE_SCREEN} {
      font-size: 17px;
    }
  `,

  // --- Body (global / non-responsive per Figma) ---
  bodyLarge: css`
    ${bodyBase}
    font-size: 22px;
    letter-spacing: -0.02em;
  `,
  // 22px body at semibold weight (use bodyLarge for light).
  bodyLargeSemibold: css`
    ${bodyBase}
    font-size: 22px;
    font-weight: ${FONT_WEIGHT.semibold};
    letter-spacing: -0.02em;
  `,
  bodyMedium: css`
    ${bodyBase}
  `,
  bodyMediumSemibold: css`
    ${bodyBase}
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 150%;
  `,
  bodySmall: css`
    ${bodyBase}
    font-size: 14px;
    line-height: 140%;
  `,
  // NOTE: absolute 20px line-height is Figma-verbatim; letter-spacing normalized to match bodySmall.
  bodySmallSemibold: css`
    ${bodyBase}
    font-size: 14px;
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: 20px;
  `,
  bodyXSmall: css`
    ${bodyBase}
    font-size: 11px;
    line-height: 140%;
  `,
  // Overline — uppercase with proportional tracking.
  overline: css`
    ${bodyBase}
    font-size: 13px;
    font-weight: ${FONT_WEIGHT.bold};
    line-height: normal;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  `,
} as const;

export type TypographyKey = keyof typeof typographyStyles;

// -----------------------------------------------------------------------------
// Button typography — bodyBase with a collapsed single-line height (overrides
// only weight + line-height).
// -----------------------------------------------------------------------------
export const buttonText = css`
  ${bodyBase}
  font-weight: ${FONT_WEIGHT.semibold};
  line-height: 1;
`;

// -----------------------------------------------------------------------------
// SHADOWS — canonical elevation scale
// Tuned against navy #030B2A so shadows read warm on the cream background.
// -----------------------------------------------------------------------------
export const SHADOWS = {
  formField: `0 4px 10px ${PALETTE.black_04}`,
  accountCard: `0 4px 20px ${PALETTE.black_03}`,
  // Decorative photo framing, not UI elevation; preserve the authored artwork effects.
  polaroid: `0 3.866px 28.993px ${PALETTE.black_03}`,
  nextStepsPhoto: `0 4.304px 16.142px ${PALETTE.black_03}`,
  nav: `4px 0px 12px -4px ${PALETTE.navy[900_08]}`,
  xs: "0px 1px 2px rgba(3, 11, 42, 0.04)",
  sm: "0px 2px 6px rgba(3, 11, 42, 0.05)",
  md: "0px 4px 12px rgba(3, 11, 42, 0.06)",
  lg: "0px 8px 24px rgba(3, 11, 42, 0.08)",
  xl: "0px 16px 40px rgba(3, 11, 42, 0.10)",
  button: "0px 4px 20px rgba(3, 11, 42, 0.06)",
  buttonHover: "0px 6px 24px rgba(3, 11, 42, 0.12)",
} as const;

// Box Shadow Constants — prefer SHADOWS.* going forward
export const BOX_SHADOW_SMALL = SHADOWS.sm;
export const BOX_SHADOW_MEDIUM = SHADOWS.md;
export const BOX_SHADOW_LARGE = SHADOWS.lg;
export const BOX_SHADOW_BUTTON = SHADOWS.button;
export const BOX_SHADOW_BUTTON_HOVER = SHADOWS.buttonHover;
export const BOX_SHADOW_ELEVATED = SHADOWS.lg;
